import {
  Component,
  ElementRef,
  inject,
  ChangeDetectionStrategy,
  ViewChild,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { InboxTaskSkeletonComponent } from '@shared/components/skeletons/inbox-task-skeleton.component';
import { StatusMessageComponent } from '@shared/components/status-message/status-message.component';
import { InboxStore } from './inbox.store';
import { InboxItemComponent } from './inbox-item.component';
import { UtilityDockStore } from '../utility-dock.store';

@Component({
  selector: 'app-inbox',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    TranslatePipe,
    InboxTaskSkeletonComponent,
    StatusMessageComponent,
    InboxItemComponent,
  ],
  templateUrl: './inbox.component.html',
  styleUrls: ['./inbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxComponent {
  protected readonly store = inject(InboxStore);
  private readonly utilityDockStore = inject(UtilityDockStore);
  private readonly router = inject(Router);
  @ViewChild('quickAddInput') private quickAddInput?: ElementRef<HTMLInputElement>;

  protected newPrivateTaskTitle = '';

  constructor() {
    effect(() => {
      const request = this.utilityDockStore.inboxQuickAddRequest();
      if (request === 0) {
        return;
      }

      this.focusQuickAddInput();
    });
  }

  protected trackByTaskId = (_: number, item: { inboxTaskId: string }): string => item.inboxTaskId;

  protected createPrivateTask(): void {
    const title = this.newPrivateTaskTitle.trim();
    if (!title) {
      this.focusQuickAddInput();
      return;
    }

    this.store.createPrivateTask(title);
    this.newPrivateTaskTitle = '';
    this.focusQuickAddInput();
  }

  protected onNavigate(taskId: string): void {
    const item = this.store.items().find((t) => t.inboxTaskId === taskId);

    if (item && String(item.kind ?? '').trim().toLowerCase() === 'private') {
      this.router.navigate(['/tasks/private', item.inboxTaskId], {
        state: { privateTask: item },
      });
      return;
    }

    if (item?.entityId) {
      this.router.navigate(['/tasks/do', item.entityId]);
      return;
    }

    // Fallback for tasks without entityId
    console.log('Navigate to task:', taskId);
  }

  private focusQuickAddInput(): void {
    setTimeout(() => {
      const input = this.quickAddInput?.nativeElement;
      if (!input) {
        return;
      }

      input.focus();
      input.select();
    });
  }
}
